import { useState, useCallback, useMemo, useEffect } from 'react';
import { ISelectorMultipleProps, TOptionsItem } from '../types';

const useFilterData = (props: ISelectorMultipleProps) => {
  const {
    value: valueProps,
    defaultValue,
    options,
    radioOptions,
    minSelect,
    maxSelect,
    onOpen,
    onClose,
    onChange,
    isRevertToLastSelection
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(valueProps || defaultValue || []);

  // 受控更新筛选器内部value值
  useEffect(() => {
    if (!valueProps) return;
    setValue(valueProps);
  }, [valueProps]);

  // firstRadioValue:radioOptions第一项 
  // optionsValueList：options筛选器value值数组
  // radioValueList：radioOptions筛选器value值数组
  const { firstRadioValue, optionsValueList, radioValueList } = useMemo(() => {
    const firstRadioValue: string = radioOptions?.[0]?.value;
    const optionsValueList: string[] = options?.map((item) => item?.value);
    const radioValueList: string[] = radioOptions?.map((item) => item?.value);
    return { firstRadioValue, optionsValueList, radioValueList };
  }, [options, radioOptions]);

  // options的选中值 radioOptions的选中值
  const { optionsValue, radioValue } = useMemo(() => {
    const optionsValue = value?.filter(valueItem => optionsValueList?.includes(valueItem)) || [];
    const radioValue = value?.find(valueItem => radioValueList?.includes(valueItem)) || '';
    return { optionsValue, radioValue };
  }, [value, optionsValueList, radioValueList]);

  // 根据minSelect和maxSelect与筛选器值长度比较将options反选禁止选中
  const disableOptions = useMemo(() => {
    if (!minSelect && !maxSelect) return options;
    const disableOptions: TOptionsItem[] = [];
    options?.forEach((item) => {
      let newItem = item;
      if (minSelect && optionsValue?.length <= minSelect) {
        newItem = optionsValue?.includes(item?.value) ? { ...item, disabled: true } : item;
      }
      if (maxSelect && optionsValue?.length >= maxSelect) {
        newItem = optionsValue?.includes(item?.value) ? item : { ...item, disabled: true };
      }
      disableOptions?.push(newItem);
    });
    return disableOptions;
  }, [maxSelect, minSelect, options, optionsValue]);

  // options选择change事件(取消最后一个选项时选中radioOptions第一个选项)
  const handleChange = useCallback((val: string[]) => {
    const filterValue = val?.filter(valueItem => optionsValueList?.includes(valueItem)) || [];
    const newRadioValue = firstRadioValue !== undefined ? [firstRadioValue] : [];
    const newValue = filterValue?.length ? filterValue : newRadioValue;
    setValue(newValue);
    onChange?.(newValue);
  }, [firstRadioValue, onChange, optionsValueList]);

  // radioOptions选择change事件
  const handleRadioChange = useCallback((val: string) => {
    setValue([val]);
    onChange?.([val]);
  }, [onChange]);

  // select all事件 disable项不可选
  // 搜索情况下 全选搜索的选项
  // 非搜索情况下 全选options的选项
  const handleSelectAll = useCallback((renderOptions: Array<TOptionsItem>) => {
    const isSearch = renderOptions?.length !== options?.length;
    let renderOptionsValues = renderOptions?.filter(v => !v?.disabled)?.map(item => item?.value) || [];
    if (isSearch) {
      const map: any = {};
      optionsValue?.forEach(item => (map[item] = true));
      renderOptions?.filter(v => !v?.disabled)?.forEach(item => (map[item?.value] = true));
      renderOptionsValues = Object.keys(map);
    };
    setValue(renderOptionsValues);
    onChange?.(renderOptionsValues);
  }, [onChange, options?.length, optionsValue]);

  // clear all事件 disable项可清除
  // 搜索情况下 清除搜索的选项
  // 非搜索情况下 清除之后选中radioOptions第一项,如果没有radioOptions就清空
  const handleClearAll = useCallback((renderOptions: Array<TOptionsItem>) => {
    const isSearch = renderOptions?.length !== options?.length;
    const newRadioValue = firstRadioValue !== undefined ? [firstRadioValue] : [];
    let renderOptionsValues = newRadioValue;
    if (isSearch) {
      const searchValueList = renderOptions?.map(item => item?.value) || [];
      const valueList = optionsValue?.filter(valueItem => !searchValueList?.includes(valueItem)) || [];
      // 如果清空options所有选项,选中radioOptions第一项
      renderOptionsValues = valueList?.length ? valueList : newRadioValue;
    }
    setValue(renderOptionsValues);
    onChange?.(renderOptionsValues);
  }, [firstRadioValue, onChange, options?.length, optionsValue]);

  // 打开下拉框
  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();
  };

  // 关闭下拉框
  const handleClose = () => {
    setIsOpen(false);
    onClose?.(value);
    if (isRevertToLastSelection) {
      setValue(valueProps);
    }
  };

  return {
    optionsValue,
    radioValue,
    options: disableOptions,
    radioOptions,
    isOpen,
    setValue,
    handleSelectAll,
    handleClearAll,
    handleOpen,
    handleClose,
    handleChange,
    handleRadioChange,
    setIsOpen
  };
};

export default useFilterData;
